import React, { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from "react";
import { debug } from "@/lib/debug";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { setSentryBrand } from "@/lib/sentry";
import { useToast } from "@/hooks/use-toast";
import { toast as sonnerToast } from "sonner";
import { fetchBrandLimitStatus } from "@/lib/brandLimits";

export interface Brand {
  id: string;
  user_id: string;
  business_name: string | null;
  signature_name: string | null;
  /** The brand's own website. Feeds the search and reputation lookups (VA-195). */
  website_url: string | null;
  /** Town, region and country. Without one, no public listing can be found (VA-195). */
  business_location: string | null;
  tone_of_voice: string | null;
  brand_promise: string | null;
  brand_values: string | null;
  brand_does: string | null;
  competitor_diff: string | null;
  usp: string | null;
  target_audience: string | null;
  pain_points: string | null;
  risks_of_inaction: string | null;
  call_to_action: string | null;
  tagline: string | null;
  secondary_tagline: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  accent_color: string | null;
  tertiary_color: string | null;
  tertiary_color_name: string | null;
  tertiary_color_meaning: string | null;
  anchor_dark_color: string | null;
  anchor_light_color: string | null;
  logo_url: string | null;
  logo_light_url: string | null;
  logo_dark_url: string | null;
  heading_font: string | null;
  body_font: string | null;
  /** Chosen look for generated images, e.g. "Bright & Airy" (VA-228). */
  image_style: string | null;
  customer_positioning: string | null;
  expert_positioning: string | null;
  brand_personality: string | null;
  brand_story: string | null;
  custom_brand_prompt: string | null;
  identity_pillars: string[] | null;
  value_propositions: string[] | null;
  special_instructions: Record<string, string> | null;
  writing_style: string | null;
  key_phrases: string[] | null;
  service_plans: Record<string, unknown> | null;
  industry: string | null;
  products_services: string | null;
  social_proof: string | null;
  brand_story_content: string | null;
  market_position: string | null;
  extraction_confidence: Record<string, string> | null;
  elevator_pitch: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface BrandContextType {
  activeBrand: Brand | null;
  activeBrandId: string | null;
  brands: Brand[];
  isLoading: boolean;
  isCreatingBrand: boolean;
  setIsCreatingBrand: (value: boolean) => void;
  setActiveBrand: (brandId: string, brandData?: Brand) => Promise<void>;
  deleteBrand: (brandId: string) => Promise<boolean>;
  duplicateBrand: (brandId: string) => Promise<string | null>;
  refetchBrands: () => Promise<void>;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

export function BrandProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { isImpersonating, impersonatedUserId } = useImpersonation();
  const { toast } = useToast();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [activeBrand, setActiveBrandState] = useState<Brand | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingBrand, setIsCreatingBrand] = useState(false);
  const hasFetchedOnce = useRef(false);

  // Effective user ID: use impersonated user when in impersonation mode
  const effectiveUserId = isImpersonating && impersonatedUserId ? impersonatedUserId : user?.id;

  const brandContentEqual = (a: Brand | null, b: Brand | null): boolean => {
    if (!a || !b) return false;
    if (a.id !== b.id) return false;
    const contentFields: (keyof Brand)[] = [
      'business_name', 'target_audience', 'tagline', 'customer_positioning',
      'expert_positioning', 'brand_promise', 'brand_values', 'usp',
      'pain_points', 'primary_color', 'value_propositions', 'identity_pillars',
      'tone_of_voice', 'brand_personality', 'secondary_color', 'tertiary_color',
      'accent_color', 'anchor_dark_color', 'anchor_light_color',
      'logo_url', 'logo_light_url', 'logo_dark_url', 'heading_font', 'body_font', 'signature_name',
      // VA-228: without this, picking a new image style compares equal and the refetch
      // discards it — the card would snap back to the old look until a hard reload.
      'image_style',
      'custom_brand_prompt', 'special_instructions', 'service_plans', 'updated_at'
    ];
    return contentFields.every(field => {
      const valA = a[field];
      const valB = b[field];
      if (Array.isArray(valA) && Array.isArray(valB)) return JSON.stringify(valA) === JSON.stringify(valB);
      if (typeof valA === 'object' && typeof valB === 'object' && valA !== null && valB !== null) return JSON.stringify(valA) === JSON.stringify(valB);
      return valA === valB;
    });
  };

  const fetchBrands = useCallback(async () => {
    if (!effectiveUserId) {
      setBrands([]);
      setActiveBrandState(null);
      setIsLoading(false);
      return;
    }

    // Only show loading spinner on initial fetch, NOT on refetches.
    // This prevents the loading flash that occurs when tab-switch triggers
    // a cascade: token refresh → session change → refetchBrands → loading flash
    if (!hasFetchedOnce.current) {
      setIsLoading(true);
    }
    try {
      const { data, error } = await supabase
        .from("brand_profiles")
        .select("*")
        .eq("user_id", effectiveUserId)
        .order("created_at", { ascending: true });

      if (error) {
        debug.error("Error fetching brands:", error);
        setBrands([]);
        setActiveBrandState(null);
        return;
      }

      const brandsData = (data || []) as Brand[];
      setBrands(brandsData);

      // CRITICAL FIX: Always update activeBrand from fresh data
      // Check if current activeBrand still exists in the new data
      const currentActiveId = activeBrand?.id;
      if (currentActiveId) {
        const updatedActiveBrand = brandsData.find((b) => b.id === currentActiveId);
        if (updatedActiveBrand) {
          // CRITICAL: Only update if data actually changed  -  prevents VisionAssistant
          // from re-running its conversation effect and resetting the chat
          setActiveBrandState(prev => {
            if (brandContentEqual(prev, updatedActiveBrand)) {
              return prev; // Content identical, keep old reference to prevent re-renders
            }
            return updatedActiveBrand;
          });
        } else {
          // Active brand was deleted, fall back to first brand or one marked active
          const newActive = brandsData.find((b) => b.is_active) || brandsData[0] || null;
          setActiveBrandState(prev => {
            if (brandContentEqual(prev, newActive)) return prev;
            return newActive;
          });
        }
      } else {
        // No current active brand, find one
        const active = brandsData.find((b) => b.is_active);
        if (active) {
          setActiveBrandState(prev => {
            if (brandContentEqual(prev, active)) return prev;
            return active;
          });
        } else if (brandsData.length > 0) {
          setActiveBrandState(prev => {
            if (brandContentEqual(prev, brandsData[0])) return prev;
            return brandsData[0];
          });
        } else {
          setActiveBrandState(null);
        }
      }
    } catch (error) {
      debug.error("Error fetching brands:", error);
      setBrands([]);
      setActiveBrandState(null);
    } finally {
      setIsLoading(false);
      hasFetchedOnce.current = true;
    }
  }, [effectiveUserId, activeBrand?.id]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  // Keep Sentry brand tag in sync with the active brand
  useEffect(() => {
    setSentryBrand(activeBrand?.id ?? null);
  }, [activeBrand?.id]);

  const setActiveBrand = useCallback(async (brandId: string, brandData?: Brand) => {
    if (!effectiveUserId) return;

    try {
      // Call the RPC function to set the brand as active
      const { error } = await supabase.rpc("set_brand_active", {
        p_brand_id: brandId,
        p_user_id: effectiveUserId,
      });

      if (error) {
        debug.error("Error setting active brand:", error);
        return;
      }

      // Track if this is a brand switch (not creation) for toast logic
      const isNewBrandCreation = !!brandData;

      // Update brands array synchronously
      setBrands((prev) => {
        const exists = prev.some((b) => b.id === brandId);
        let updatedBrands: Brand[];
        
        if (brandData && !exists) {
          updatedBrands = [...prev, { ...brandData, is_active: true }].map((b) => ({
            ...b,
            is_active: b.id === brandId,
          }));
        } else {
          updatedBrands = prev.map((b) => ({
            ...b,
            is_active: b.id === brandId,
          }));
        }
        
        return updatedBrands;
      });

      // Set active brand state SYNCHRONOUSLY (no setTimeout)
      const newActiveBrand = brandData || brands.find((b) => b.id === brandId);
      if (newActiveBrand) {
        setActiveBrandState({ ...newActiveBrand, is_active: true });
        
        // Only show "Brand switched" toast for switching, not creation
        // (creation has its own success toast in CreateBrandModal)
        if (!isNewBrandCreation) {
          sonnerToast.success("Brand switched", {
            description: `Now using "${newActiveBrand.business_name || 'Unnamed Brand'}"`,
          });
        }
      }
    } catch (error) {
      debug.error("Error setting active brand:", error);
    }
  }, [effectiveUserId, brands]);

  const deleteBrand = useCallback(async (brandId: string): Promise<boolean> => {
    if (!effectiveUserId) return false;

    // Prevent deleting the last brand
    if (brands.length <= 1) {
      toast({
        title: "Cannot delete",
        description: "You must have at least one brand.",
        variant: "destructive",
      });
      return false;
    }

    try {
      const { error } = await supabase
        .from("brand_profiles")
        .delete()
        .eq("id", brandId)
        .eq("user_id", effectiveUserId);

      if (error) {
        debug.error("Error deleting brand:", error);
        toast({
          title: "Error",
          description: "Failed to delete brand. Please try again.",
          variant: "destructive",
        });
        return false;
      }

      // OPTIMISTIC UPDATE: Immediately remove from local state
      const remainingBrands = brands.filter((b) => b.id !== brandId);
      setBrands(remainingBrands);

      // If we deleted the active brand, set another one as active
      const wasActive = activeBrand?.id === brandId;
      if (wasActive && remainingBrands.length > 0) {
        const newActiveBrand = remainingBrands[0];
        setActiveBrandState(newActiveBrand);
        
        // Update database to reflect new active brand
        await supabase.rpc("set_brand_active", {
          p_brand_id: newActiveBrand.id,
          p_user_id: effectiveUserId,
        });
      } else if (wasActive) {
        // No brands left (shouldn't happen due to guard, but handle gracefully)
        setActiveBrandState(null);
      }

      // Background refetch for eventual consistency (non-blocking)
      fetchBrands().catch((err) => debug.error("Background refetch failed:", err));

      return true;
    } catch (error) {
      debug.error("Error deleting brand:", error);
      toast({
        title: "Error",
        description: "Failed to delete brand. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  }, [effectiveUserId, brands, activeBrand?.id, fetchBrands, toast]);

  const duplicateBrand = useCallback(async (brandId: string): Promise<string | null> => {
    if (!effectiveUserId) return null;

    try {
      const limitStatus = await fetchBrandLimitStatus(effectiveUserId);
      if (!limitStatus.canCreate) {
        sonnerToast.error("Brand limit reached", {
          description: limitStatus.reachedMessage || "Upgrade your plan to add more brands.",
        });
        return null;
      }

      // Find the brand to duplicate
      const brandToDuplicate = brands.find(b => b.id === brandId);
      if (!brandToDuplicate) {
        toast({
          title: "Error",
          description: "Brand not found.",
          variant: "destructive",
        });
        return null;
      }

      // Create new brand with copied data (exclude id, timestamps, is_active)
      const { id: _bid, created_at: _bca, updated_at: _bua, is_active: _bia, ...insertableBrandData } = brandToDuplicate;
      
      const newName = `${insertableBrandData.business_name || 'Unnamed Brand'} (Copy)`;
      
      const { data: newBrand, error: brandError } = await supabase
        .from("brand_profiles")
        .insert({
          ...insertableBrandData as any,
          business_name: newName,
          user_id: effectiveUserId,
          is_active: false,
        })
        .select()
        .single();

      if (brandError || !newBrand) {
        debug.error("Error creating duplicated brand:", brandError);
        throw brandError;
      }

      // Fetch and duplicate customer avatar if exists
      const { data: avatar } = await supabase
        .from("customer_avatars")
        .select("*")
        .eq("brand_id", brandId)
        .maybeSingle();

      if (avatar) {
        const { 
          id: avatarId, 
          created_at: avatarCreated, 
          updated_at: avatarUpdated,
          brand_id, 
          ...avatarData 
        } = avatar;
        
        await supabase.from("customer_avatars").insert({
          ...avatarData,
          brand_id: newBrand.id,
          user_id: effectiveUserId,
        });
      }

      // Refetch brands to update state
      await fetchBrands();
      
      sonnerToast.success("Brand duplicated", {
        description: `Created "${newName}"`,
      });

      return newBrand.id;
    } catch (error) {
      debug.error("Error duplicating brand:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to duplicate brand. Please try again.",
        variant: "destructive",
      });
      return null;
    }
  }, [effectiveUserId, brands, fetchBrands, toast]);

  const value: BrandContextType = {
    activeBrand,
    activeBrandId: activeBrand?.id ?? null,
    brands,
    isLoading,
    isCreatingBrand,
    setIsCreatingBrand,
    setActiveBrand,
    deleteBrand,
    duplicateBrand,
    refetchBrands: fetchBrands,
  };

  return (
    <BrandContext.Provider value={value}>
      {children}
    </BrandContext.Provider>
  );
}

const BRAND_FALLBACK: BrandContextType = {
  activeBrand: null,
  activeBrandId: null,
  brands: [],
  isLoading: true,
  isCreatingBrand: false,
  setIsCreatingBrand: () => {},
  setActiveBrand: async () => {},
  deleteBrand: async () => false,
  duplicateBrand: async () => null,
  refetchBrands: async () => {},
};

export function useBrand() {
  const context = useContext(BrandContext);
  if (context === undefined) {
    console.warn("[useBrand] Called outside BrandProvider — returning safe defaults. This is expected during HMR but should not happen in production.");
    return BRAND_FALLBACK;
  }
  return context;
}
